import { useEffect, useMemo, useState } from "react";
import {
  FINANCIAL_YEAR_CUSTOM_SELECT_VALUE,
  FINANCIAL_YEAR_MIN_START,
  formatFinancialYear,
  getMaxAllowedFinancialYearEnd,
  getRecentAustralianFinancialYears,
  isValidCustomFinancialYear,
  parseFinancialYear,
} from "../utils/financialYears";

type FinancialYearFieldProps = {
  id?: string;
  label?: string;
  value: string;
  onChange: (financialYear: string) => void;
  selectClassName?: string;
  labelClassName?: string;
  recentCount?: number;
  disabled?: boolean;
};

export default function FinancialYearField({
  id = "financialYear",
  label = "Financial year",
  value,
  onChange,
  selectClassName = "form-select",
  labelClassName = "form-label",
  recentCount = 6,
  disabled = false,
}: FinancialYearFieldProps) {
  const recentOptions = useMemo(
    () => getRecentAustralianFinancialYears(recentCount),
    [recentCount],
  );

  const presetValues = useMemo(
    () => new Set(recentOptions.map((o) => o.value)),
    [recentOptions],
  );

  const [customMode, setCustomMode] = useState(false);
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  const maxEndYear = getMaxAllowedFinancialYearEnd();

  const selectOptions = useMemo(() => {
    if (
      value &&
      !presetValues.has(value) &&
      parseFinancialYear(value) &&
      !customMode
    ) {
      const parsed = parseFinancialYear(value)!;
      return [
        ...recentOptions,
        {
          value,
          label: value,
          startYear: parsed.start,
          endYear: parsed.end,
        },
      ];
    }
    return recentOptions;
  }, [value, presetValues, recentOptions, customMode]);

  useEffect(() => {
    if (!value) {
      if (customMode) return;
      setCustomStart("");
      setCustomEnd("");
      return;
    }

    if (presetValues.has(value)) {
      setCustomMode(false);
      return;
    }

    const parsed = parseFinancialYear(value);
    if (parsed) {
      setCustomMode(true);
      setCustomStart(String(parsed.start));
      setCustomEnd(String(parsed.end));
    }
  }, [value, presetValues, customMode]);

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = e.target.value;
    if (next === FINANCIAL_YEAR_CUSTOM_SELECT_VALUE) {
      const parsed = parseFinancialYear(value);
      setCustomMode(true);
      if (parsed) {
        setCustomStart(String(parsed.start));
        setCustomEnd(String(parsed.end));
      } else {
        setCustomStart("");
        setCustomEnd("");
      }
      return;
    }
    setCustomMode(false);
    setCustomStart("");
    setCustomEnd("");
    onChange(next);
  };

  const applyCustomYears = (startRaw: string, endRaw: string) => {
    setCustomStart(startRaw);
    setCustomEnd(endRaw);
    const start = Number(startRaw);
    const end = Number(endRaw);
    if (isValidCustomFinancialYear(start, end)) {
      onChange(formatFinancialYear(start, end));
    } else if (!startRaw && !endRaw) {
      onChange("");
    }
  };

  const customParsed =
    customStart && customEnd
      ? { start: Number(customStart), end: Number(customEnd) }
      : null;
  const customValid =
    customParsed != null &&
    isValidCustomFinancialYear(customParsed.start, customParsed.end);
  const customTouched = Boolean(customStart || customEnd);

  const selectValue = customMode
    ? FINANCIAL_YEAR_CUSTOM_SELECT_VALUE
    : value || "";

  return (
    <div>
      <label htmlFor={id} className={labelClassName}>
        {label}
      </label>
      <select
        id={id}
        name="financialYear"
        className={selectClassName}
        value={selectValue}
        onChange={handleSelectChange}
        disabled={disabled}
      >
        <option value="" disabled>
          Select financial year
        </option>
        {selectOptions.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
        <option value={FINANCIAL_YEAR_CUSTOM_SELECT_VALUE}>
          Custom financial year…
        </option>
      </select>

      {customMode && (
        <div className="row g-2 mt-2">
          <div className="col-6">
            <label htmlFor={`${id}-start`} className="form-label small mb-1">
              Start year
            </label>
            <input
              id={`${id}-start`}
              type="number"
              className="form-control"
              min={FINANCIAL_YEAR_MIN_START}
              max={maxEndYear - 1}
              step={1}
              placeholder="e.g. 2019"
              value={customStart}
              disabled={disabled}
              onChange={(e) => applyCustomYears(e.target.value, customEnd)}
            />
          </div>
          <div className="col-6">
            <label htmlFor={`${id}-end`} className="form-label small mb-1">
              End year
            </label>
            <input
              id={`${id}-end`}
              type="number"
              className="form-control"
              min={FINANCIAL_YEAR_MIN_START + 1}
              max={maxEndYear}
              step={1}
              placeholder="e.g. 2020"
              value={customEnd}
              disabled={disabled}
              onChange={(e) => applyCustomYears(customStart, e.target.value)}
            />
          </div>
          {customTouched && !customValid && (
            <div className="col-12">
              <p className="small text-danger mb-0">
                Enter a valid AU financial year from {FINANCIAL_YEAR_MIN_START}-
                {FINANCIAL_YEAR_MIN_START + 1} through {maxEndYear - 1}-
                {maxEndYear}. End year must be start year + 1.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
